import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, MessageSquareText } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

const empty = { title: "", content: "" };

export default function SavedReplies() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/saved-replies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };
  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and message are required"); return; }
    setBusy(true);
    try {
      if (editingId) { await api.put(`/saved-replies/${editingId}`, form); toast.success("Saved reply updated"); }
      else { await api.post("/saved-replies", form); toast.success("Saved reply added"); }
      reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const edit = (r) => { setEditingId(r.reply_id); setForm({ title: r.title || "", content: r.content || "" }); };
  const del = async (id) => { if (!window.confirm("Delete this saved reply?")) return; await api.delete(`/saved-replies/${id}`); toast.success("Deleted"); load(); };

  return (
    <>
      <PageHeader eyebrow="Inbox" title="Saved Replies"
        subtitle="Quick replies you can insert in a human chat (e.g. ask for song type, genre, deity)" />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[#E9EDEF] bg-white rounded-lg" data-testid="saved-replies-list">
          <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center justify-between">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">Your Saved Replies</h3>
            <span className="font-mono text-[10px] text-[#8696A0]">{items.length}</span>
          </div>
          <div className="divide-y divide-[#E9EDEF]">
            {items.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-[#8696A0]">
                <MessageSquareText className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />
                No saved replies yet. Add reusable messages to speed up human chats.
              </p>
            )}
            {items.map((r) => (
              <div key={r.reply_id} className="flex items-start gap-3 px-5 py-3" data-testid={`saved-reply-row-${r.reply_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#111B21] truncate">{r.title}</p>
                  <p className="text-xs text-[#54656F] mt-1 whitespace-pre-wrap break-words">{r.content}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => edit(r)} data-testid={`saved-reply-edit-${r.reply_id}`} className="text-[#8696A0] hover:text-[#075E54] p-1"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r.reply_id)} data-testid={`saved-reply-delete-${r.reply_id}`} className="text-[#8696A0] hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3 h-fit">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">{editingId ? "Edit Reply" : "Add Saved Reply"}</h3>
            {editingId && <button onClick={reset} className="text-[#8696A0] hover:text-[#111B21]" data-testid="saved-reply-cancel"><X className="h-4 w-4" /></button>}
          </div>
          <input data-testid="saved-reply-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Ask song details)" className={inputCls} />
          <textarea data-testid="saved-reply-content" rows={7} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Message text (e.g. To get started, could you share the type of bhajan/song, the genre, and the deity or theme?)" className={inputCls} />
          <button data-testid="saved-reply-save" onClick={save} disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Plus className="h-4 w-4" /> {editingId ? "Save Changes" : "Add Reply"}
          </button>
        </div>
      </div>
    </>
  );
}
